import dayjs from 'dayjs';
import * as H from 'history';
import { Action } from 'modules/types/common';
import queryString from 'query-string';
import Config from 'react-native-config';
import analyticsService, {
  AmplitudeEventData,
  AnalyticsEvent,
  CDP_ID_KEY
} from 'services/AnalyticsService.web';
import { getAppSettings } from 'store/appSettings/actions';
import AuthServices, { formatAuthForStorage, getAuthToken } from 'services/AuthServices.web';
import Logger from 'services/Logger/Logger';
import { identifyUser } from 'store/analytics/actions';
import {
  authenticateTPSWithAuth,
  authenticateUser,
  authenticateUserFail,
  AUTHENTICATE_USER_SUCCESS,
  getUserSession,
  logout,
  setUserSession
} from 'store/authentication/actions.web';
import { DFD_AUTH_CONFIG, LOCAL_STORAGE_TOKEN } from 'store/authentication/configs.web';
import { AuthResult } from 'store/authentication/reducers';
import { LoginType } from 'store/authentication/types';
import { getEvent, isEventExpired } from 'store/events/actions';
import {
  getTermsAndConditions,
  GET_DEPS,
  GET_DEPS_ERROR,
  GET_DEPS_SUCCESS
} from 'store/global/actions';
import { getMyDoctors } from 'store/myDoctors/actions';
import { navigationWebStateChange } from 'store/navigation/actions.web';
import { getProfile } from 'store/profile/actions';
import { RootDispatch } from 'store/types';
import { RedirectBrand, getRedirectIdx, getRegisterBrand } from 'store/register';

export type PathType =
  | 'ANY'
  | 'LOADING'
  | 'UNAUTHENTICATED'
  | 'AUTHENTICATED'
  | 'EITHER_VALID'
  | 'AUTH_LITE';

interface BootstrapAppType {
  pathname: string;
  pathType: PathType;
  history: H.History;
}

/**
 * Method: handleAuthenticationCallback
 * Description: Handles the authentication callbacks whether there is anything to handle or not.
 */
export async function handleAuthenticationCallback(
  pathname: string,
  dispatch: RootDispatch,
  apikey: string
): Promise<'auth' | 'unauth'> {
  try {
    // Handle authentication verification
    const urlParams = queryString.parse(window?.location?.search) || { code: '' };
    if (urlParams.code) {
      const res = await getAuthToken(DFD_AUTH_CONFIG, urlParams.code as string);
      const dfdAuth = (formatAuthForStorage(res) as unknown) as AuthResult;

      if (!!dfdAuth?.accessToken) {
        dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: { dfdAuth } });
        // setAuth({ dfdAuth, visitPayAuth: null });
        if (pathname.includes('ping')) {
          localStorage.setItem(LOCAL_STORAGE_TOKEN, dfdAuth.accessToken);
          setUserSession(dfdAuth, apikey);
          await dispatch(authenticateTPSWithAuth(dfdAuth.accessToken, apikey));
        } else if (pathname.includes('visit-pay')) {
          // TODO: This was not being handled, will it ever need be?
        }
        return 'auth';
      }
    }
    return 'unauth';
  } catch (err) {
    dispatch(authenticateUserFail());
    return 'unauth';
  }
}

/**
 * Method: getDependencies
 * Description: gets the necessary deps for an authenticated user
 */
export async function getDependencies(dispatch: RootDispatch): Promise<Action> {
  dispatch({ type: GET_DEPS });
  try {
    const [profileRes] = await Promise.all([dispatch(getProfile())]);
    if (profileRes.error) {
      throw profileRes.error;
    }
    const anaRes = await dispatch(identifyUser());

    if (anaRes.error) {
      throw anaRes.error;
    }

    dispatch(getMyDoctors());

    return dispatch({ type: GET_DEPS_SUCCESS });
  } catch (error) {
    return dispatch({ type: GET_DEPS_ERROR, error });
  }
}

/**
 * Method: handleAuthenticated
 * Description: This handles the resolution of an thaenticatied user by PathType
 */
export async function handleAuthenticated(
  dispatch: RootDispatch,
  history: H.History,
  pathType: PathType,
  pathname?: string,
  loginType?: LoginType
) {
  const startTime = dayjs();
  const getDepsRes = await getDependencies(dispatch);
  const endTime = dayjs();
  const cdpId = localStorage.getItem(CDP_ID_KEY);
  let loadTime = endTime.diff(startTime, 's');
  if (loadTime < 1) loadTime = 1;
  let loginStatus = 'success';

  if (getDepsRes.error) {
    loginStatus = `fail: ${getDepsRes.error?.config?.reduxSourceAction?.type}`;
  }

  dispatch(navigationWebStateChange('authenticated'));

  if (!!cdpId) {
    localStorage.removeItem(CDP_ID_KEY);
    analyticsService.setUserAttribute(CDP_ID_KEY, cdpId);
  }

  const data: AmplitudeEventData = {
    referringUrl: 'AuthLoading',
    currentUrl: 'Loading',
    application: 'DFD',
    load_time: loadTime,
    login_type: loginType || LoginType.PASSWORD,
    login_success: loginStatus
  };
  analyticsService.logEvent(AnalyticsEvent.SignInCompleted, data);

  // Note: Auth Light - Authenticated flow. Case: If a user clicks the link and has a MHP account.
  if (pathType === 'AUTH_LITE' && Config.ALLOW_AUTH_LIGHT === 'enabled') {
    try {
      const pathArr = pathname?.split('/');
      const eventId = pathArr?.length && pathArr.slice(-1)[0];
      if (!eventId) {
        throw new Error('No Event Id');
      } else {
        // Get
        const event = await dispatch(getEvent(eventId)).then(
          (response: Action) => response?.payload?.data
        );
        if (!event?.sourceId) {
          throw new Error('Invalid Event');
        }

        if (event.sourceResource === 'labResult') {
          // Note: navigate to labResult screen
          history.push(`/u/health-record/test-results/${event.sourceId}`);
        } else {
          history.push('/');
        }
      }
    } catch (err) {
      // Invalid event, navigate to dashboard.
      history.push('/');
    }
  } else if (pathType !== 'LOADING') {
    // handle original Route
    history.push(pathname || '');
  } else {
    const storedPath = localStorage.getItem('pathAfterLogin');
    if (storedPath) {
      // Remove pathAfterLogin so we don't go there after a logout
      localStorage.removeItem('pathAfterLogin');
      history.push(storedPath);
    } else {
      history.push('/');
    }
  }
}

/**
 * Method: handleUnauthenticated
 * Description: this handles resolving an unauthentication path by the pathType
 */
export async function handleUnauthenticated(
  history: H.History,
  dispatch: RootDispatch,
  pathType: PathType,
  pathname: string
) {
  dispatch(navigationWebStateChange('unauthenticated'));
  localStorage.removeItem(CDP_ID_KEY);
  if (pathType === 'AUTHENTICATED') {
    // Go to login page
    localStorage.setItem('pathAfterLogin', pathname); // set path after login
    history.push('/landing');
  } else if (pathType === 'AUTH_LITE' && Config.ALLOW_AUTH_LIGHT === 'enabled') {
    try {
      const pathArr = pathname?.split('/');
      // Note: pathType === 'AUTH_LITE' should always have events in path
      const idx = pathArr.findIndex(val => val === 'events');

      const eventId = pathArr[idx + 1];
      if (!eventId) {
        throw new Error('No Event Id');
      } else {
        const payloadData = await dispatch(isEventExpired(eventId)).then(
          (response: Action) => response?.payload?.data
        );
        localStorage.removeItem('pathAfterLogin');
        if (payloadData?.expired === false) {
          history.push(pathname);
        } else if (payloadData?.expired) {
          history.push('/al/link-expired');
        }
      }
    } catch (err) {
      // Invalid event, navigate to dashboard.
      history.push('/');
    }
  } else if (pathType === 'LOADING') {
    // Handle user typed in loading location
    localStorage.removeItem('pathAfterLogin');
    history.push('/landing');
  } else {
    localStorage.removeItem('pathAfterLogin');
    // handle unauthenticated location
    history.push(pathname);
  }
}

/**
 * Method: boostrapApp
 * Description: Start HERE. This is the entry point of this logic. It Loads the app to the approprate state, and path
 */
export const boostrapApp = ({ pathname, pathType, history }: BootstrapAppType) => async (
  dispatch: RootDispatch
): Promise<null> => {
  try {
    // Save redirect index if it has it
    // Get the existing brand and redirect_index to protect against a refresh
    const registerBrand = getRegisterBrand();
    const redirectIdx = getRedirectIdx();

    const urlParams = queryString.parse(window?.location?.search) || {};

    const {
      branding = registerBrand || RedirectBrand.MHP,
      redirect_index = redirectIdx || '000'
    } = urlParams;

    window.sessionStorage.setItem('registerBrand', branding as string);
    window.sessionStorage.setItem('redirectIdx', encodeURI(redirect_index as string));
    window.sessionStorage.setItem('redirectIdxTimeStamp', Date.now().toString());

    // get past session key
    const localSessionKey = localStorage.getItem(LOCAL_STORAGE_TOKEN) as string;
    // Get app settings
    // Catch app Settings to stop the crazy loop if it fails
    const appSettings = await dispatch(getAppSettings()).catch(() => console.info('catching'));
    const { apikey } = appSettings?.payload?.data?.shared || {};
    if (appSettings?.error || !apikey) {
      dispatch({ type: 'BOOTSTRAP_FAIL' });
      return null;
    }
    dispatch(getTermsAndConditions());

    // Handle previous login
    if (pathType === 'LOADING') {
      // Handle returning from authentication or user typing in a loading path
      const authResult = await handleAuthenticationCallback(pathname, dispatch, apikey);

      if (authResult === 'auth') {
        await handleAuthenticated(dispatch, history, pathType, pathname);
      } else if (authResult === 'unauth') {
        // Auth is expired or user typed in a loading url
        await handleUnauthenticated(history, dispatch, pathType, pathname);
      }
    } else if (localSessionKey) {
      const userSession = await getUserSession(localSessionKey, apikey);
      if (userSession) {
        // Check for user expired authentication
        if (!AuthServices.isAuthenticated(userSession)) {
          await handleUnauthenticated(history, dispatch, pathType, pathname);
        } else {
          // Handle previos auth
          const dfdLocalAuthResult = await dispatch({
            type: AUTHENTICATE_USER_SUCCESS,
            payload: { dfdAuth: userSession }
          });
          // Check TPS
          await dispatch(authenticateTPSWithAuth(userSession.accessToken, apikey));
          if (dfdLocalAuthResult.payload) {
            await handleAuthenticated(dispatch, history, pathType, pathname, LoginType.STORED_AUTH);
          } else {
            await handleUnauthenticated(history, dispatch, pathType, pathname);
          }
        }
      } else {
        // No previous session on record
        await handleUnauthenticated(history, dispatch, pathType, pathname);
      }
    } else {
      // Handle no Auth
      await handleUnauthenticated(history, dispatch, pathType, pathname);
    }
  } catch (err) {
    Logger.warn('login error: ', err);
    dispatch(logout());
  }
  return null;
};

/**
 * Method: handleLoginPress
 * Description: It handles the login action that is preformed through the app.
 */
export const handleLoginPress = (history: H.History, currentUrl?: string) => (
  dispatch: RootDispatch
) => {
  if (currentUrl) {
    analyticsService.logEvent(AnalyticsEvent.SignInStarted, {
      currentUrl
    });
  }
  // Depracated: No need for this navigation before authentication
  // history.push(`/loading`);
  dispatch(authenticateUser());
};
